import React, { useState } from "react";
import { PlusCircleIcon, PencilSquareIcon, TrashIcon, ArrowPathIcon } from "@heroicons/react/24/solid";
import {
  createLiveLink,
  updateLiveLink,
  useAllLiveLinks,
  useMeetingTemplates,
  useNotificationTemplates,
  useNotificationConfig,
  updateNotificationConfig,
  useSendQueueStatus,
  previewRecipients,
  sendOneOffEmail,
  sendTestNotification,
  sendNowNotification,
  fetchSentCampaigns,
} from "../../util/db";
import { useAuth } from "../../util/auth";
import { apiRequest } from "../../util/util";
import { Timestamp } from "firebase/firestore";
import ReactQuill from "react-quill";

const PLAN_OPTIONS = [
  { label: "Anyone", plans: [], checkKey: null },
  { label: "Stairs & Buckets", plans: ["sby", "sbm", "sbm1"], checkKey: "sbm" },
  { label: "Million Dollar Tool Kit", plans: ["procm", "proc"], checkKey: "proc" },
  { label: "Million Dollar Road Map", plans: ["tp", "tpl", "tpm"], checkKey: "tp" },
  { label: "All Access", plans: ["life"], checkKey: "life" },
  { label: "+Agent License", plans: ["agent_license"], checkKey: "agent_license" },
];

function getTimestampFromDateAndTime(dateStr, timeStr) {
  if (!dateStr || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return null;
  if (!timeStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hours, minutes] = timeStr.split(":").map(Number);
  const d = new Date();
  d.setFullYear(year, month - 1, day);
  d.setHours(hours, minutes, 0, 0);
  return Timestamp.fromDate(d);
}

function toISOString(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hours, minutes] = timeStr.split(":").map(Number);
  const d = new Date();
  d.setFullYear(year, month - 1, day);
  d.setHours(hours, minutes, 0, 0);
  return d.toISOString();
}

function getDurationMinutes(startTimeStr, finishTimeStr) {
  if (!startTimeStr || !finishTimeStr) return 60;
  const [sh, sm] = startTimeStr.split(":").map(Number);
  const [fh, fm] = finishTimeStr.split(":").map(Number);
  return Math.max((fh * 60 + fm) - (sh * 60 + sm), 15);
}

function formatTimestamp(ts) {
  if (!ts || !ts.seconds) return "";
  const d = new Date(ts.seconds * 1000);
  return d.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function tsToDateInput(ts) {
  if (!ts || !ts.seconds) return "";
  const d = new Date(ts.seconds * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function tsToTimeInput(ts) {
  if (!ts || !ts.seconds) return "";
  const d = new Date(ts.seconds * 1000);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function togglePlanGroup(currentPlans, groupPlans) {
  const hasGroup = groupPlans.length > 0 && groupPlans.every((p) => currentPlans.includes(p));
  if (hasGroup) {
    return currentPlans.filter((p) => !groupPlans.includes(p));
  }
  return [...currentPlans.filter((p) => !groupPlans.includes(p)), ...groupPlans];
}

function computeTriggerAt(offsetType, offsetMinutes, eventStartDate) {
  if (offsetType === "relative" && offsetMinutes != null) {
    return new Date(eventStartDate.getTime() + offsetMinutes * 60 * 1000);
  }
  const eventDay = new Date(eventStartDate);
  if (offsetType === "monday_6am") {
    const dayOfWeek = eventDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(eventDay);
    monday.setDate(monday.getDate() - daysToSubtract);
    monday.setHours(6, 0, 0, 0);
    return monday;
  }
  if (offsetType === "morning_6am") {
    const morning = new Date(eventDay);
    morning.setHours(6, 0, 0, 0);
    return morning;
  }
  if (offsetType === "next_day_11am") {
    const nextDay = new Date(eventDay);
    nextDay.setDate(nextDay.getDate() + 1);
    nextDay.setHours(11, 0, 0, 0);
    return nextDay;
  }
  return eventStartDate;
}

function generateNotifications(template, eventStartDate) {
  if (!template || !template.notifications) return [];
  return template.notifications.map((notif, i) => {
    const triggerDate = computeTriggerAt(
      notif.offsetType, notif.offsetMinutes, eventStartDate,
    );
    return {
      id: `notif_${i + 1}`,
      label: notif.label,
      notificationType: notif.notificationType,
      triggerAt: Timestamp.fromDate(triggerDate),
      status: "pending",
      sentAt: null,
      sentCount: 0,
      failedCount: 0,
    };
  });
}

function isPlanGroupActive(currentPlans, option) {
  if (option.plans.length === 0) {
    return currentPlans.length === 0;
  }
  return option.checkKey && currentPlans.includes(option.checkKey);
}

function formatRequiredPlans(requiredPlans) {
  if (!requiredPlans || requiredPlans.length === 0) return "Anyone";
  const activeLabels = PLAN_OPTIONS
    .filter((option) => option.plans.length > 0)
    .filter((option) => option.plans.every((plan) => requiredPlans.includes(plan)))
    .map((option) => option.label);
  return activeLabels.length > 0 ? activeLabels.join(", ") : requiredPlans.join(", ");
}

export default function EventManager() {
  const auth = useAuth();
  const { data: events, status: eventsStatus } = useAllLiveLinks();
  const { data: meetingTemplates } = useMeetingTemplates();
  const { data: notificationTemplates } = useNotificationTemplates();
  const { data: notifConfig } = useNotificationConfig();

  // Notification settings state
  const [showSettings, setShowSettings] = useState(false);
  const [settingsBatchSize, setSettingsBatchSize] = useState(50);
  const [settingsInterval, setSettingsInterval] = useState(15);
  const [settingsTestRecipients, setSettingsTestRecipients] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  // Zoom API state
  const [zoomMeetingId, setZoomMeetingId] = useState(null);
  const [zoomOccurrenceId, setZoomOccurrenceId] = useState(null);
  const [zoomMeetings, setZoomMeetings] = useState([]);
  const [showZoomPicker, setShowZoomPicker] = useState(false);
  const [zoomLoading, setZoomLoading] = useState(false);
  const [zoomError, setZoomError] = useState(null);

  // Modal state
  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [longDescription, setLongDescription] = useState("");
  const [eventRecap, setEventRecap] = useState("");
  const [link, setLink] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [finishTime, setFinishTime] = useState("");
  const [eventType, setEventType] = useState("");
  const [useZoom, setUseZoom] = useState(true);
  const [requiredPlans, setRequiredPlans] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  // Notification status overrides (for edit form)
  const [notifStatuses, setNotifStatuses] = useState({});

  // Campaign state (for events with notifications array)
  const [campaignPaused, setCampaignPaused] = useState(false);
  const [editingNotifications, setEditingNotifications] = useState(null);

  // Audience preview
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // One-off email/SMS compose
  const [showCompose, setShowCompose] = useState(false);
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeSmsBody, setComposeSmsBody] = useState("");
  const [composeChannel, setComposeChannel] = useState("email");
  const [composeFrom, setComposeFrom] = useState("noreply");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [activeBatchGroupId, setActiveBatchGroupId] = useState(null);
  const { data: batchJobs } = useSendQueueStatus(activeBatchGroupId);

  // Notification preview
  const [previewNotifType, setPreviewNotifType] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [sendingNow, setSendingNow] = useState(null); // index of notification being sent

  // Sent campaign audit log
  const [sentCampaigns, setSentCampaigns] = useState([]);

  // Delete confirmation
  const [deletingId, setDeletingId] = useState(null);
  const [deletingEvent, setDeletingEvent] = useState(null);

  // Zoom sync
  const [syncingAll, setSyncingAll] = useState(false);

  // Past events
  const [showPast, setShowPast] = useState(false);
  const [pastPage, setPastPage] = useState(1);
  const PAST_PAGE_SIZE = 10;

  function resetForm() {
    setTitle("");
    setDescription("");
    setLongDescription("");
    setEventRecap("");
    setLink("");
    setDate("");
    setStartTime("");
    setFinishTime("");
    setEventType("");
    setRequiredPlans([]);
    setEditingId(null);
    setZoomMeetingId(null);
    setZoomOccurrenceId(null);
    setUseZoom(true);
    setNotifStatuses({});
    setSelectedTemplateId("");
    setCampaignPaused(false);
    setEditingNotifications(null);
    setPreviewData(null);
    setPreviewLoading(false);
    setShowCompose(false);
    setComposeSubject("");
    setComposeBody("");
    setComposeSmsBody("");
    setComposeChannel("email");
    setComposeFrom("noreply");
    setSending(false);
    setSendResult(null);
    setActiveBatchGroupId(null);
    setPreviewNotifType(null);
    setSendingTest(false);
    setTestResult(null);
    setSentCampaigns([]);
  }

  function openCreate() {
    resetForm();
    setIsOpen(true);
  }

  function openEdit(event) {
    setEditingId(event.id);
    setTitle(event.title || "");
    setDescription(event.description || "");
    setLongDescription(event.longDescription || "");
    setEventRecap(event.eventRecap || "");
    setEventType(event.eventType || "");
    setDate(tsToDateInput(event.startTime));
    setStartTime(tsToTimeInput(event.startTime));
    setFinishTime(tsToTimeInput(event.finishTime));
    setRequiredPlans(event.requiredPlans || []);
    setLink(event.link || "");
    setZoomMeetingId(event.zoomMeetingId || null);
    setZoomOccurrenceId(event.zoomOccurrenceId || null);
    setUseZoom(!!event.zoomMeetingId);
    setNotifStatuses({
      notificationMondayBefore: event.notificationMondayBefore || null,
      notificationMorningOf: event.notificationMorningOf || null,
      notificationTenMinute: event.notificationTenMinute || null,
      notificationDayAfter: event.notificationDayAfter || null,
    });
    setCampaignPaused(event.campaignPaused || false);
    setEditingNotifications(
      event.notifications && Array.isArray(event.notifications)
        ? event.notifications.map((n) => ({ ...n }))
        : null,
    );
    setShowCompose(false);
    setComposeSubject("");
    setComposeBody("");
    setComposeSmsBody("");
    setComposeChannel("email");
    setComposeFrom("noreply");
    setSending(false);
    setSendResult(null);
    setActiveBatchGroupId(null);
    setSelectedTemplateId("");
    setPreviewData(null);
    fetchSentCampaigns(event.id).then(setSentCampaigns).catch(() => {});
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
    resetForm();
  }

  async function handleFetchZoomMeetings() {
    setZoomLoading(true);
    setZoomError(null);
    try {
      const result = await apiRequest("zoom-meetings", "POST", {
        action: "listMeetings",
      });
      // Filter out meetings already imported
      const imported = new Set(
        (events || []).map((e) =>
          e.zoomOccurrenceId
            ? `${e.zoomMeetingId}_${e.zoomOccurrenceId}`
            : `${e.zoomMeetingId}`
        )
      );
      const filtered = (result.meetings || []).filter((m) => {
        const key = m.occurrence_id ? `${m.id}_${m.occurrence_id}` : `${m.id}`;
        return !imported.has(key);
      });
      setZoomMeetings(filtered);
      setShowZoomPicker(true);
    } catch (err) {
      setZoomError(err.message || "Failed to fetch Zoom meetings");
    } finally {
      setZoomLoading(false);
    }
  }

  function handleSelectZoomMeeting(meeting) {
    setTitle(meeting.topic || "");
    setZoomMeetingId(meeting.id);
    setZoomOccurrenceId(meeting.occurrence_id || null);
    setLink(meeting.registration_url || meeting.join_url || "");
    // Parse Zoom start_time into date/time inputs
    if (meeting.start_time) {
      const d = new Date(meeting.start_time);
      setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
      setStartTime(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
      // Set finish time based on duration
      const endMs = d.getTime() + (meeting.duration || 60) * 60 * 1000;
      const end = new Date(endMs);
      setFinishTime(`${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`);
    }
    setShowZoomPicker(false);
    setIsOpen(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const st = getTimestampFromDateAndTime(date, startTime);
      const ft = getTimestampFromDateAndTime(date, finishTime);
      if (!st || !ft) return;

      let currentZoomMeetingId = zoomMeetingId;
      let currentLink = link;
      let zoomStartUrl = null;

      const isoStart = toISOString(date, startTime);
      const duration = getDurationMinutes(startTime, finishTime);

      try {
        if (editingId && currentZoomMeetingId) {
          // Update existing Zoom meeting (occurrence-level if recurring)
          const result = await apiRequest("zoom-meetings", "POST", {
            action: "updateMeeting",
            data: {
              meetingId: currentZoomMeetingId,
              occurrenceId: zoomOccurrenceId,
              topic: title,
              startTime: isoStart,
              duration,
              eventId: editingId,
            },
          });
          currentLink = result.registration_url || result.join_url || currentLink;
        } else if (!editingId && !currentZoomMeetingId && useZoom) {
          // Create new Zoom meeting for new events
          const result = await apiRequest("zoom-meetings", "POST", {
            action: "createMeeting",
            data: { topic: title, startTime: isoStart, duration },
          });
          currentZoomMeetingId = result.id;
          currentLink = result.registration_url || result.join_url;
          zoomStartUrl = result.start_url;
        }
      } catch (err) {
        const proceed = window.confirm(
          `Zoom sync failed: ${err.message}\n\nSave the event without Zoom sync?`
        );
        if (!proceed) return;
      }

      const eventData = {
        title,
        description: description || "",
        longDescription: longDescription || "",
        eventRecap: eventRecap || "",
        link: currentLink,
        startTime: st,
        finishTime: ft,
        requiredPlans,
        eventType,
        zoomMeetingId: currentZoomMeetingId || null,
        zoomOccurrenceId: zoomOccurrenceId || null,
      };

      if (zoomStartUrl) {
        eventData.zoomStartUrl = zoomStartUrl;
      }

      if (editingId) {
        // Include any notification status overrides
        const notifUpdates = {};
        if (editingNotifications) {
          // Array-based notifications: save campaignPaused + notifications array
          notifUpdates.campaignPaused = campaignPaused;
          notifUpdates.notifications = editingNotifications;
        } else {
          // Legacy flat fields
          for (const [key, value] of Object.entries(notifStatuses)) {
            if (value !== null) notifUpdates[key] = value;
          }
        }
        await updateLiveLink(editingId, { ...eventData, ...notifUpdates });
      } else {
        const selectedTemplate = selectedTemplateId &&
          (meetingTemplates || []).find((t) => t.id === selectedTemplateId);
        if (selectedTemplate) {
          const eventStartDate = st.toDate();
          const notifications = generateNotifications(selectedTemplate, eventStartDate);
          await createLiveLink({
            ...eventData,
            createdBy: auth.user.uid,
            meetingTemplateId: selectedTemplateId,
            campaignPaused: false,
            notifications,
          });
        } else {
          await createLiveLink({
            ...eventData,
            createdBy: auth.user.uid,
            notificationMondayBefore: "pending",
            notificationMorningOf: "pending",
            notificationTenMinute: "pending",
            notificationDayAfter: "pending",
          });
        }
      }
      closeModal();
    } catch (err) {
      console.error("Failed to save event:", err);
      alert("Failed to save event: " + (err.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    setDeletingId("loading");
    try {
      if (deletingEvent?.zoomMeetingId) {
        // Server-side delete: handles both Zoom + Firestore via Admin SDK
        await apiRequest("zoom-meetings", "POST", {
          action: "deleteMeeting",
          data: {
            meetingId: deletingEvent.zoomMeetingId,
            occurrenceId: deletingEvent.zoomOccurrenceId,
            eventId: id,
          },
        });
      } else {
        // No Zoom link — delete via Admin SDK too
        await apiRequest("zoom-meetings", "POST", {
          action: "deleteEvent",
          data: { eventId: id },
        });
      }
    } catch (err) {
      console.error("Failed to delete event:", err);
      alert("Failed to delete event: " + err.message);
    } finally {
      setDeletingId(null);
      setDeletingEvent(null);
    }
  }

  async function handleSyncFromZoom(event) {
    try {
      await apiRequest("zoom-meetings", "POST", {
        action: "syncMeeting",
        data: {
          meetingId: event.zoomMeetingId,
          occurrenceId: event.zoomOccurrenceId,
          eventId: event.id,
        },
      });
    } catch (err) {
      alert("Sync failed: " + (err.message || "Unknown error"));
    }
  }

  async function handleSyncAll() {
    const zoomEvents = (events || []).filter((e) => e.zoomMeetingId);
    if (zoomEvents.length === 0) return;
    setSyncingAll(true);
    let failed = 0;
    for (const event of zoomEvents) {
      try {
        await apiRequest("zoom-meetings", "POST", {
          action: "syncMeeting",
          data: {
            meetingId: event.zoomMeetingId,
            occurrenceId: event.zoomOccurrenceId,
            eventId: event.id,
          },
        });
      } catch {
        failed++;
      }
    }
    setSyncingAll(false);
    if (failed > 0) {
      alert(`Synced ${zoomEvents.length - failed} of ${zoomEvents.length} events. ${failed} failed.`);
    }
  }

  async function handlePreviewRecipients() {
    if (!eventType) return;
    setPreviewLoading(true);
    try {
      const data = await previewRecipients(eventType, requiredPlans);
      setPreviewData(data);
    } catch (err) {
      console.error("Failed to preview recipients:", err);
      alert("Failed to load recipients: " + (err.message || "Unknown error"));
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleSendOneOff(opts = {}) {
    const needsEmail = composeChannel === "email" || composeChannel === "both";
    const needsSms = composeChannel === "sms" || composeChannel === "both";
    if (!editingId) return;
    if (needsEmail && (!composeSubject || !composeBody)) {
      alert("Email requires a subject and body.");
      return;
    }
    if (needsSms && !composeSmsBody) {
      alert("SMS requires a message body.");
      return;
    }
    const isTest = opts.testMode || false;
    if (!isTest) {
      const ok = window.confirm(
        `Send this email to all ${eventType} recipients? This cannot be undone.`,
      );
      if (!ok) return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const result = await sendOneOffEmail({
        eventId: editingId,
        subject: composeSubject,
        htmlBody: composeBody,
        textBody: composeBody.replace(/<[^>]*>/g, ""),
        channel: composeChannel || "email",
        fromEmail: composeFrom || "noreply",
        smsBody: composeSmsBody || "",
        testMode: isTest,
        testRecipients: opts.testRecipients || [],
      });
      setSendResult(result);
      if (result.batchGroupId) {
        setActiveBatchGroupId(result.batchGroupId);
      }
      if (!isTest) {
        setComposeSubject("");
        setComposeBody("");
        setComposeSmsBody("");
      }
      fetchSentCampaigns(editingId).then(setSentCampaigns).catch(() => {});
    } catch (err) {
      console.error("Failed to send:", err);
      alert("Failed to send: " + (err.message || "Unknown error"));
    } finally {
      setSending(false);
    }
  }

  async function handleSendNow(idx) {
    if (!editingId) return;
    if (!window.confirm("Send this notification now? This will immediately queue messages to all eligible recipients.")) return;
    setSendingNow(idx);
    try {
      await sendNowNotification({ eventId: editingId, notificationIndex: idx });
      alert("Notification sent successfully. Status will update shortly.");
    } catch (err) {
      console.error("Send Now failed:", err);
      alert("Send Now failed: " + (err.message || "Unknown error"));
    } finally {
      setSendingNow(null);
    }
  }

  async function handleSendNowLegacy(fieldKey) {
    if (!editingId) return;
    if (!window.confirm("Send this notification now? This will immediately queue messages to all eligible recipients.")) return;
    setSendingNow(fieldKey);
    try {
      await sendNowNotification({ eventId: editingId, legacyField: fieldKey });
      alert("Notification sent successfully. Status will update shortly.");
    } catch (err) {
      console.error("Send Now failed:", err);
      alert("Send Now failed: " + (err.message || "Unknown error"));
    } finally {
      setSendingNow(null);
    }
  }

  function renderVars(str, vars) {
    if (!str) return "";
    return str.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] || `{{${key}}}`);
  }

  function getEventVars() {
    const eventStart = date && startTime
      ? getTimestampFromDateAndTime(date, startTime) : null;
    const eventDate = eventStart ? new Date(eventStart.seconds * 1000) : new Date();
    const formattedTime = eventDate.toLocaleString("en-US", {
      weekday: "long", year: "numeric", month: "long",
      day: "numeric", hour: "numeric", minute: "2-digit",
      hour12: true, timeZone: "America/Denver",
    }) + " MST";
    const shortTime = eventDate.toLocaleString("en-US", {
      hour: "numeric", minute: "2-digit",
      hour12: true, timeZone: "America/Denver",
    }) + " MST";
    return {
      eventTitle: title || "",
      eventDescription: description || "",
      eventLongDescription: longDescription || "",
      eventRecap: eventRecap || "",
      eventTime: formattedTime,
      eventTimeShort: shortTime,
      eventLink: link || "",
    };
  }

  function findTemplate(notifType) {
    if (!notificationTemplates || !eventType) return null;
    return notificationTemplates.find(
      (t) => t.eventType === eventType && t.notificationType === notifType && t.active !== false,
    );
  }

  async function handleSendTestNotification(notifType) {
    if (!editingId || !auth.user?.email) return;
    setSendingTest(true);
    setTestResult(null);
    try {
      // Keep test sends aligned with current form edits.
      await updateLiveLink(editingId, {
        title: title || "",
        description: description || "",
        longDescription: longDescription || "",
        eventRecap: eventRecap || "",
        link: link || "",
      });
      const result = await sendTestNotification({
        eventId: editingId,
        notificationType: notifType,
        testEmail: auth.user.email,
        testPhone: auth.user.phone || "",
      });
      setTestResult({
        type: notifType,
        ...result,
      });
    } catch (err) {
      console.error("Failed to send test notification:", err);
      alert("Failed to send test: " + (err.message || "Unknown error"));
    } finally {
      setSendingTest(false);
    }
  }

  function confirmDelete(event) {
    setDeletingId(event.id);
    setDeletingEvent(event);
  }

  function handlePlanToggle(option) {
    if (option.plans.length === 0) {
      setRequiredPlans([]);
    } else {
      const newPlans = togglePlanGroup(requiredPlans, option.plans);
      setRequiredPlans(newPlans);
    }
  }

  const now = Math.floor(Date.now() / 1000);
  const upcomingEvents = events ? events.filter((e) => e.finishTime && e.finishTime.seconds > now) : [];
  const pastEvents = events ? events.filter((e) => e.finishTime && e.finishTime.seconds <= now) : [];

  return (
    <>
      {/* Zoom Integration Section */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Zoom Integration</h3>
        <p className="text-sm text-gray-500 mb-4">
          Import existing meetings from Zoom or create them automatically when scheduling events.
        </p>
        <div className="flex gap-3">
          <button
            onClick={handleFetchZoomMeetings}
            disabled={zoomLoading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 disabled:opacity-50"
          >
            {zoomLoading ? "Loading..." : "Pull Meetings from Zoom"}
          </button>
          <button
            onClick={handleSyncAll}
            disabled={syncingAll}
            className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 disabled:opacity-50"
          >
            {syncingAll ? "Syncing..." : "Sync All from Zoom"}
          </button>
        </div>
        {zoomError && (
          <p className="text-sm text-red-600 mt-2">{zoomError}</p>
        )}
      </div>

      {/* Notification Settings */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Notification Settings</h3>
          <button
            onClick={() => {
              if (!showSettings && notifConfig) {
                setSettingsBatchSize(notifConfig.batchSize || 50);
                setSettingsInterval(notifConfig.batchIntervalMinutes || 15);
                setSettingsTestRecipients(
                  (notifConfig.defaultTestRecipients || [])
                    .map((r) => [r.email, r.phone].filter(Boolean).join(","))
                    .join("\n")
                );
              }
              setShowSettings(!showSettings);
            }}
            className="px-3 py-1.5 rounded text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
          >
            {showSettings ? "Hide" : "Configure"}
          </button>
        </div>
        {!showSettings && notifConfig && (
          <p className="text-sm text-gray-500 mt-2">
            Batch size: {notifConfig.batchSize || 50} | Interval: {notifConfig.batchIntervalMinutes || 15}min
            {notifConfig.defaultTestRecipients?.length > 0 && ` | ${notifConfig.defaultTestRecipients.length} test recipients`}
          </p>
        )}
        {showSettings && (
          <div className="mt-3 space-y-3">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Batch Size</label>
                <input
                  type="number"
                  value={settingsBatchSize}
                  onChange={(e) => setSettingsBatchSize(Number(e.target.value))}
                  min={1}
                  max={500}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Interval (minutes)</label>
                <input
                  type="number"
                  value={settingsInterval}
                  onChange={(e) => setSettingsInterval(Number(e.target.value))}
                  min={1}
                  max={120}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Default Test Recipients (one per line: email,phone)
              </label>
              <textarea
                value={settingsTestRecipients}
                onChange={(e) => setSettingsTestRecipients(e.target.value)}
                rows={3}
                placeholder={"test@example.com,+15551234567\nadmin@example.com"}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 font-mono"
              />
            </div>
            <button
              onClick={async () => {
                setSavingSettings(true);
                try {
                  const testRecips = settingsTestRecipients
                    .split("\n")
                    .map((line) => line.trim())
                    .filter(Boolean)
                    .map((line) => {
                      const parts = line.split(",").map((s) => s.trim());
                      return { email: parts[0] || "", phone: parts[1] || "" };
                    });
                  await updateNotificationConfig({
                    batchSize: settingsBatchSize,
                    batchIntervalMinutes: settingsInterval,
                    defaultTestRecipients: testRecips,
                  });
                } catch (err) {
                  alert("Failed to save: " + err.message);
                } finally {
                  setSavingSettings(false);
                }
              }}
              disabled={savingSettings}
              className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 disabled:opacity-50"
            >
              {savingSettings ? "Saving..." : "Save Settings"}
            </button>
          </div>
        )}
      </div>

      {/* Zoom Meeting Picker Modal */}
      {showZoomPicker && (
        <div className="z-30 fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Select a Zoom Meeting</h3>
              <button onClick={() => setShowZoomPicker(false)} className="text-2xl text-gray-400">&times;</button>
            </div>
            {zoomMeetings.length === 0 ? (
              <p className="text-gray-500 text-sm py-4">No upcoming Zoom meetings found.</p>
            ) : (
              <div className="space-y-2">
                {zoomMeetings.map((meeting) => (
                  <button
                    key={meeting.id}
                    onClick={() => handleSelectZoomMeeting(meeting)}
                    className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900">{meeting.topic}</p>
                      {meeting._recurring && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700">
                          Recurring
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {meeting.start_time ? new Date(meeting.start_time).toLocaleString("en-US", {
                        weekday: "short", month: "short", day: "numeric",
                        hour: "numeric", minute: "2-digit", hour12: true,
                      }) : "No time set"}
                      {meeting.duration ? ` (${meeting.duration} min)` : ""}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Schedule Event Button */}
      <button
        type="button"
        onClick={openCreate}
        className="relative flex flex-col items-center block min-w-[280px] rounded-lg border-2 border-dashed border-green-700 p-12 text-center hover:border-green-500 focus:outline-none text-green-700 hover:text-green-500 hover:bg-green-50"
      >
        <PlusCircleIcon className="h-12 justify-center" />
        <span className="mt-2 block text-sm font-semibold text-gray-900">Schedule Event</span>
      </button>

      {/* Event List */}
      {eventsStatus === "success" && events && (
        <div className="mt-6 space-y-2">
          {upcomingEvents.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Upcoming Events</h3>
              {upcomingEvents.map((event) => (
                <EventRow key={event.id} event={event} onEdit={openEdit} onDelete={confirmDelete} onSync={handleSyncFromZoom} />
              ))}
            </>
          )}
          {pastEvents.length > 0 && (
            <>
              <button
                onClick={() => { setShowPast(!showPast); setPastPage(1); }}
                className="flex items-center gap-2 mt-6 group"
              >
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider group-hover:text-gray-600">
                  Past Events ({pastEvents.length})
                </h3>
                <span className="text-gray-400 text-xs group-hover:text-gray-600">
                  {showPast ? "\u25B2" : "\u25BC"}
                </span>
              </button>
              {showPast && (
                <>
                  {pastEvents.slice(0, pastPage * PAST_PAGE_SIZE).map((event) => (
                    <EventRow key={event.id} event={event} onEdit={openEdit} onDelete={confirmDelete} onSync={handleSyncFromZoom} past />
                  ))}
                  {pastPage * PAST_PAGE_SIZE < pastEvents.length && (
                    <button
                      onClick={() => setPastPage(pastPage + 1)}
                      className="w-full py-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      Show more ({pastEvents.length - pastPage * PAST_PAGE_SIZE} remaining)
                    </button>
                  )}
                </>
              )}
            </>
          )}
          {events.length === 0 && (
            <p className="text-gray-500 text-sm py-4">No events yet. Click "Schedule Event" to create one.</p>
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      {deletingEvent && (
        <div className="z-30 fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full text-center">
            <h3 className="text-lg font-semibold mb-2">Delete Event?</h3>
            <p className="text-gray-500 text-sm mb-4">
              This will remove the event from the app. The Zoom meeting will not be affected.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setDeletingId(null); setDeletingEvent(null); }}
                disabled={deletingId === "loading"}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deletingEvent.id)}
                disabled={deletingId === "loading"}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deletingId === "loading" ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isOpen && (
        <div className="z-20 fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg shadow-lg lg:w-1/2 w-3/4 max-w-4xl relative max-h-[90vh] overflow-y-auto">
            <h2 className="text-center text-3xl font-bold pb-4">
              {editingId ? "Edit Event" : "Schedule Event"}
            </h2>
            <button onClick={closeModal} className="absolute top-4 right-4 text-3xl text-gray-400 rounded-md">
              &times;
            </button>

            <div className="h-0 my-4 border-t-4 border-dotted border-gray-200" />

            {/* Title */}
            <p className="text-gray-400 text-left m-2">Title</p>
            <div className="mb-2">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="The Public Facing Title"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>

            {/* Short Description */}
            <p className="text-gray-400 text-left m-2">Short Description <span className="text-xs">({"{{eventDescription}}"} in templates)</span></p>
            <div className="mb-2">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="One-liner for SMS and email subjects (optional)"
                rows={2}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>

            {/* Long Description */}
            <p className="text-gray-400 text-left m-2">Long Description <span className="text-xs">({"{{eventLongDescription}}"} in templates)</span></p>
            <div className="mb-2">
              <textarea
                value={longDescription}
                onChange={(e) => setLongDescription(e.target.value)}
                placeholder="Detailed description for email body content (optional)"
                rows={4}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>

            {/* Event Recap */}
            <p className="text-gray-400 text-left m-2">Event Recap <span className="text-xs">({"{{eventRecap}}"} in templates — used in day-after email)</span></p>
            <div className="mb-2">
              <ReactQuill
                theme="snow"
                value={eventRecap}
                onChange={setEventRecap}
                placeholder="Training recap content for the day-after email (optional)"
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link"],
                    ["clean"],
                  ],
                }}
              />
            </div>

            {/* Event Type */}
            <p className="text-gray-400 text-left m-2">Event Type</p>
            <span className="isolate lg:inline-flex rounded-md mb-4">
              <button
                onClick={() => setEventType("IAT")}
                type="button"
                className={`relative inline-flex items-center rounded-full ${eventType === "IAT" ? "bg-blue-100 text-blue-700 ring-blue-600" : "bg-white text-gray-400 ring-gray-300"} px-4 py-2 mr-2 text-sm font-medium ring-1 ring-inset hover:bg-gray-50 focus:z-10`}
              >
                IAT Event
              </button>
              <button
                onClick={() => setEventType("I247")}
                type="button"
                className={`relative inline-flex items-center rounded-full ${eventType === "I247" ? "bg-purple-100 text-purple-700 ring-purple-600" : "bg-white text-gray-400 ring-gray-300"} px-4 py-2 mr-2 text-sm font-medium ring-1 ring-inset hover:bg-gray-50 focus:z-10`}
              >
                I247 Event
              </button>
            </span>

            {/* Meeting Template (new events only) */}
            {!editingId && meetingTemplates && meetingTemplates.length > 0 && (
              <>
                <p className="text-gray-400 text-left m-2">Reminder Template</p>
                <span className="isolate lg:inline-flex rounded-md mb-4 flex-wrap">
                  <button
                    onClick={() => setSelectedTemplateId("")}
                    type="button"
                    className={`relative inline-flex items-center rounded-full ${!selectedTemplateId ? "bg-green-100 text-green-700 ring-green-600" : "bg-white text-gray-400 ring-gray-300"} px-3 py-2 mr-2 mb-2 text-sm ring-1 ring-inset hover:bg-gray-50 focus:z-10`}
                  >
                    Default (4 reminders)
                  </button>
                  {meetingTemplates.map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => setSelectedTemplateId(tpl.id)}
                      type="button"
                      className={`relative inline-flex items-center rounded-full ${selectedTemplateId === tpl.id ? "bg-green-100 text-green-700 ring-green-600" : "bg-white text-gray-400 ring-gray-300"} px-3 py-2 mr-2 mb-2 text-sm ring-1 ring-inset hover:bg-gray-50 focus:z-10`}
                    >
                      {tpl.name} ({tpl.notifications?.length || 0})
                    </button>
                  ))}
                </span>
              </>
            )}

            {/* Zoom / Custom Link Toggle — only shown for new events */}
            {!editingId && (
              <div className="mb-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => { setUseZoom(true); setLink(""); }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium ${useZoom ? "bg-indigo-100 text-indigo-700" : "bg-white text-gray-400 ring-1 ring-inset ring-gray-300"}`}
                >
                  Zoom Meeting
                </button>
                <button
                  type="button"
                  onClick={() => { setUseZoom(false); setZoomMeetingId(null); setLink(""); }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium ${!useZoom ? "bg-green-100 text-green-700" : "bg-white text-gray-400 ring-1 ring-inset ring-gray-300"}`}
                >
                  Custom Link
                </button>
              </div>
            )}

            {/* Zoom Link */}
            <p className="text-gray-400 text-left m-2">{zoomMeetingId ? "Zoom Link" : "Event Link"}</p>
            <div className="mb-2">
              <input
                type="text"
                value={link}
                readOnly={!!zoomMeetingId}
                onChange={(e) => setLink(e.target.value)}
                placeholder={useZoom ? "Auto-generated from Zoom" : "https://meet.google.com/..."}
                className={`block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 ${zoomMeetingId ? "bg-gray-100 text-gray-500" : "bg-white text-gray-900"}`}
              />
            </div>
            {zoomMeetingId ? (
              <p className="text-xs text-green-600 mb-2">
                Linked to Zoom Meeting #{zoomMeetingId}. Changes will sync to Zoom.
              </p>
            ) : useZoom ? (
              <p className="text-xs text-gray-400 mb-2">
                A Zoom meeting will be created automatically when you save.
              </p>
            ) : (
              <p className="text-xs text-gray-400 mb-2">
                Enter a meeting link manually (Google Meet, Teams, etc.)
              </p>
            )}

            {/* Date */}
            <p className="text-gray-400 text-left m-2">Date</p>
            <div className="mb-2">
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>

            {/* Start/Finish Time */}
            <div className="flex flex-row justify-around">
              <div>
                <p className="text-gray-400 text-left m-2">Start Time</p>
                <div className="mb-2">
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div>
                <p className="text-gray-400 text-left m-2">Finish Time</p>
                <div className="mb-2">
                  <input
                    type="time"
                    value={finishTime}
                    onChange={(e) => setFinishTime(e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>
            <p className="text-green-700 text-center text-sm">Please note the above times are in 24-hour time.</p>

            {/* Plan Visibility */}
            <p className="text-gray-400 text-left m-2">Select a Permission Level</p>
            <span className="isolate lg:inline-flex rounded-md mb-4 flex-wrap">
              {PLAN_OPTIONS.map((option) => (
                <button
                  key={option.label}
                  onClick={() => handlePlanToggle(option)}
                  type="button"
                  className={`relative inline-flex items-center rounded-full ${isPlanGroupActive(requiredPlans, option) ? "bg-green-100 text-green-700 ring-green-600" : "bg-white text-gray-400 ring-gray-300"} px-3 py-2 mr-2 mb-2 text-sm ring-1 ring-inset hover:bg-gray-50 focus:z-10`}
                >
                  {option.label}
                </button>
              ))}
            </span>

            {/* Notification Schedule Preview (new events with template) */}
            {!editingId && selectedTemplateId && date && startTime && (() => {
              const selectedTemplate = (meetingTemplates || []).find((t) => t.id === selectedTemplateId);
              if (!selectedTemplate) return null;
              const eventStartDate = getTimestampFromDateAndTime(date, startTime);
              if (!eventStartDate) return null;
              const preview = generateNotifications(selectedTemplate, eventStartDate.toDate());
              return (
                <>
                  <div className="h-0 my-4 border-t-4 border-dotted border-gray-200" />
                  <p className="text-gray-400 text-left m-2">Notification Schedule</p>
                  <div className="mb-4 rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {preview.map((n) => (
                      <div key={n.id} className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-sm text-gray-700">{n.label}</span>
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(n.triggerAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mb-2">
                    {selectedTemplate.description}
                  </p>
                </>
              );
            })()}

            {/* Campaign Controls (edit mode, array-based notifications) */}
            {editingId && editingNotifications && (
              <>
                <div className="h-0 my-4 border-t-4 border-dotted border-gray-200" />
                <div className="flex items-center justify-between mb-2">
                  <p className="text-gray-400 text-left m-2">Campaign</p>
                  <button
                    type="button"
                    onClick={() => setCampaignPaused((prev) => !prev)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                      campaignPaused
                        ? "bg-red-100 text-red-700 hover:bg-red-200"
                        : "bg-green-100 text-green-700 hover:bg-green-200"
                    }`}
                  >
                    {campaignPaused ? "Paused — Click to Resume" : "Active — Click to Pause All"}
                  </button>
                </div>
                <div className={`mb-4 rounded-lg border border-gray-200 divide-y divide-gray-100 ${campaignPaused ? "opacity-50" : ""}`}>
                  {editingNotifications.map((notif, idx) => {
                    const { icon } = notifIcon(notif.status);
                    const canToggle = notif.status === "pending" || notif.status === "paused";
                    const canRetry = notif.status === "failed";
                    const isPreviewOpen = previewNotifType === notif.notificationType;
                    const tpl = findTemplate(notif.notificationType);
                    const vars = getEventVars();
                    return (
                      <div key={notif.id}>
                        <div className="flex items-center justify-between px-4 py-2.5">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm">{icon}</span>
                            <span className="text-sm text-gray-700 truncate">{notif.label}</span>
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              {notif.triggerAt && formatTimestamp(notif.triggerAt)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <button
                              type="button"
                              onClick={() => {
                                setPreviewNotifType(isPreviewOpen ? null : notif.notificationType);
                                setTestResult(null);
                              }}
                              className="text-xs px-2 py-1 rounded font-medium text-sky-600 hover:bg-sky-50"
                            >
                              {isPreviewOpen ? "Close" : "Preview"}
                            </button>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              notif.status === "complete" ? "bg-green-100 text-green-700" :
                              notif.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                              notif.status === "paused" ? "bg-gray-100 text-gray-500" :
                              notif.status === "processing" ? "bg-blue-100 text-blue-700" :
                              notif.status === "queued" || notif.status === "queued_test" ? "bg-indigo-100 text-indigo-700" :
                              notif.status === "failed" ? "bg-red-100 text-red-700" :
                              "bg-gray-100 text-gray-400"
                            }`}>
                              {notif.status}
                            </span>
                            {canToggle && (
                              <button
                                type="button"
                                onClick={() => setEditingNotifications((prev) =>
                                  prev.map((n, i) => i === idx
                                    ? { ...n, status: n.status === "pending" ? "paused" : "pending" }
                                    : n
                                  )
                                )}
                                className={`text-xs px-2 py-1 rounded font-medium ${
                                  notif.status === "pending"
                                    ? "text-gray-500 hover:bg-gray-100"
                                    : "text-indigo-600 hover:bg-indigo-50"
                                }`}
                              >
                                {notif.status === "pending" ? "Pause" : "Resume"}
                              </button>
                            )}
                            {canRetry && (
                              <button
                                type="button"
                                onClick={() => setEditingNotifications((prev) =>
                                  prev.map((n, i) => i === idx
                                    ? { ...n, status: "pending" }
                                    : n
                                  )
                                )}
                                className="text-xs px-2 py-1 rounded font-medium text-orange-600 hover:bg-orange-50"
                              >
                                Retry
                              </button>
                            )}
                            {(notif.status === "paused" ||
                              notif.status === "failed" ||
                              (notif.status === "pending" && notif.triggerAt?.toDate?.() < new Date())
                            ) && (
                              <button
                                type="button"
                                onClick={() => handleSendNow(idx)}
                                disabled={sendingNow === idx}
                                className="text-xs px-2 py-1 rounded font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
                              >
                                {sendingNow === idx ? "Sending..." : "Send Now"}
                              </button>
                            )}
                          </div>
                        </div>
                        {isPreviewOpen && (
                          <div className="px-4 pb-3 bg-gray-50">
                            {tpl ? (
                              <>
                                <div className="mb-2">
                                  <span className="text-xs font-medium text-gray-500">Subject:</span>
                                  <p className="text-sm text-gray-800 bg-white rounded p-2 mt-1 border border-gray-200">
                                    {renderVars(tpl.subject, vars)}
                                  </p>
                                </div>
                                {tpl.htmlBody && (
                                  <div className="mb-2">
                                    <span className="text-xs font-medium text-gray-500">Email Preview:</span>
                                    <div
                                      className="email-preview-content mt-1 border border-gray-200 rounded bg-white p-2 max-h-64 overflow-y-auto text-sm"
                                      dangerouslySetInnerHTML={{ __html: renderVars(tpl.htmlBody, vars) }}
                                    />
                                  </div>
                                )}
                                {tpl.smsBody && (
                                  <div className="mb-2">
                                    <span className="text-xs font-medium text-gray-500">SMS Preview:</span>
                                    <p className="text-sm text-gray-800 bg-green-50 border border-green-200 rounded p-2 mt-1 font-mono">
                                      {renderVars(tpl.smsBody, vars)}
                                    </p>
                                  </div>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                  <button
                                    type="button"
                                    onClick={() => handleSendTestNotification(notif.notificationType)}
                                    disabled={sendingTest}
                                    className="text-xs px-3 py-1.5 rounded font-medium bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-50"
                                  >
                                    {sendingTest ? "Sending..." : `Send Test to ${auth.user?.email || "me"}`}
                                  </button>
                                  {testResult && testResult.type === notif.notificationType && (
                                    <span className="text-xs text-green-600">
                                      {testResult.emailSent ? "Email sent" : ""}{testResult.emailSent && testResult.smsSent ? " + " : ""}{testResult.smsSent ? "SMS sent" : ""}
                                    </span>
                                  )}
                                </div>
                              </>
                            ) : (
                              <p className="text-xs text-gray-400 py-2">
                                No active template found for this notification type.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Notification Status (edit mode only, legacy flat fields) */}
            {editingId && !editingNotifications && Object.values(notifStatuses).some((v) => v) && (
              <>
                <div className="h-0 my-4 border-t-4 border-dotted border-gray-200" />
                <p className="text-gray-400 text-left m-2">Notifications</p>
                <div className="mb-4 rounded-lg border border-gray-200 divide-y divide-gray-100">
                  {[
                    { key: "notificationMondayBefore", label: "Monday Before (6 AM)", notifType: "mondayBefore" },
                    { key: "notificationMorningOf", label: "Morning Of (6 AM)", notifType: "morningOf" },
                    { key: "notificationTenMinute", label: "10 Min Before", notifType: "tenMinBefore" },
                    { key: "notificationDayAfter", label: "Day After (11 AM)", notifType: "dayAfter" },
                  ].map(({ key, label, notifType }) => {
                    const status = notifStatuses[key];
                    if (!status) return null;
                    const { icon } = notifIcon(status);
                    const canToggle = status === "pending" || status === "paused";
                    const canRetry = status === "failed";
                    const isPreviewOpen = previewNotifType === notifType;
                    const tpl = findTemplate(notifType);
                    const vars = getEventVars();
                    return (
                      <div key={key}>
                        <div className="flex items-center justify-between px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{icon}</span>
                            <span className="text-sm text-gray-700">{label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setPreviewNotifType(isPreviewOpen ? null : notifType);
                                setTestResult(null);
                              }}
                              className="text-xs px-2 py-1 rounded font-medium text-sky-600 hover:bg-sky-50"
                            >
                              {isPreviewOpen ? "Close" : "Preview"}
                            </button>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              status === "complete" ? "bg-green-100 text-green-700" :
                              status === "pending" ? "bg-yellow-100 text-yellow-700" :
                              status === "paused" ? "bg-gray-100 text-gray-500" :
                              status === "processing" ? "bg-blue-100 text-blue-700" :
                              status === "failed" ? "bg-red-100 text-red-700" :
                              "bg-gray-100 text-gray-400"
                            }`}>
                              {status}
                            </span>
                            {canToggle && (
                              <button
                                type="button"
                                onClick={() => setNotifStatuses((prev) => ({
                                  ...prev,
                                  [key]: status === "pending" ? "paused" : "pending",
                                }))}
                                className={`text-xs px-2 py-1 rounded font-medium ${
                                  status === "pending"
                                    ? "text-gray-500 hover:bg-gray-100"
                                    : "text-indigo-600 hover:bg-indigo-50"
                                }`}
                              >
                                {status === "pending" ? "Pause" : "Resume"}
                              </button>
                            )}
                            {canRetry && (
                              <button
                                type="button"
                                onClick={() => setNotifStatuses((prev) => ({
                                  ...prev,
                                  [key]: "pending",
                                }))}
                                className="text-xs px-2 py-1 rounded font-medium text-orange-600 hover:bg-orange-50"
                              >
                                Retry
                              </button>
                            )}
                            {(status === "paused" || status === "failed" || status === "pending") && (
                              <button
                                type="button"
                                onClick={() => handleSendNowLegacy(key)}
                                disabled={sendingNow === key}
                                className="text-xs px-2 py-1 rounded font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
                              >
                                {sendingNow === key ? "Sending..." : "Send Now"}
                              </button>
                            )}
                          </div>
                        </div>
                        {isPreviewOpen && (
                          <div className="px-4 pb-3 bg-gray-50">
                            {tpl ? (
                              <>
                                <div className="mb-2">
                                  <span className="text-xs font-medium text-gray-500">Subject:</span>
                                  <p className="text-sm text-gray-800 bg-white rounded p-2 mt-1 border border-gray-200">
                                    {renderVars(tpl.subject, vars)}
                                  </p>
                                </div>
                                {tpl.htmlBody && (
                                  <div className="mb-2">
                                    <span className="text-xs font-medium text-gray-500">Email Preview:</span>
                                    <div
                                      className="email-preview-content mt-1 border border-gray-200 rounded bg-white p-2 max-h-64 overflow-y-auto text-sm"
                                      dangerouslySetInnerHTML={{ __html: renderVars(tpl.htmlBody, vars) }}
                                    />
                                  </div>
                                )}
                                {tpl.smsBody && (
                                  <div className="mb-2">
                                    <span className="text-xs font-medium text-gray-500">SMS Preview:</span>
                                    <p className="text-sm text-gray-800 bg-green-50 border border-green-200 rounded p-2 mt-1 font-mono">
                                      {renderVars(tpl.smsBody, vars)}
                                    </p>
                                  </div>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                  <button
                                    type="button"
                                    onClick={() => handleSendTestNotification(notifType)}
                                    disabled={sendingTest}
                                    className="text-xs px-3 py-1.5 rounded font-medium bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-50"
                                  >
                                    {sendingTest ? "Sending..." : `Send Test to ${auth.user?.email || "me"}`}
                                  </button>
                                  {testResult && testResult.type === notifType && (
                                    <span className="text-xs text-green-600">
                                      {testResult.emailSent ? "Email sent" : ""}{testResult.emailSent && testResult.smsSent ? " + " : ""}{testResult.smsSent ? "SMS sent" : ""}
                                    </span>
                                  )}
                                </div>
                              </>
                            ) : (
                              <p className="text-xs text-gray-400 py-2">
                                No active template found for this notification type.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Audience Preview */}
            {eventType && (
              <>
                <div className="h-0 my-4 border-t-4 border-dotted border-gray-200" />
                <div className="flex items-center justify-between mb-2">
                  <p className="text-gray-400 text-left m-2">Audience Preview</p>
                  <button
                    type="button"
                    onClick={handlePreviewRecipients}
                    disabled={previewLoading}
                    className="px-3 py-1.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200 disabled:opacity-50"
                  >
                    {previewLoading ? "Loading..." : previewData ? "Refresh" : "Preview Recipients"}
                  </button>
                </div>
                {previewData && (
                  <div className="mb-4 rounded-lg border border-gray-200 p-4">
                    <div className="flex gap-3 mb-3 text-sm flex-wrap">
                      <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                        {previewData.total} total
                      </span>
                      {eventType === "IAT" && (
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                          {previewData.lifeCount} Life
                        </span>
                      )}
                      <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                        {previewData.agentCount} Agent License
                      </span>
                      {previewData.phoneCount > 0 && (
                        <span className="px-2 py-1 rounded-full bg-sky-100 text-sky-700">
                          {previewData.phoneCount} with phone
                        </span>
                      )}
                    </div>
                    {(previewData.dndAllCount > 0 || previewData.dndEmailCount > 0 || previewData.dndSmsCount > 0) && (
                      <div className="flex gap-3 mb-3 text-xs flex-wrap">
                        {previewData.dndAllCount > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                            {previewData.dndAllCount} DND (all)
                          </span>
                        )}
                        {previewData.dndEmailCount > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-600">
                            {previewData.dndEmailCount} DND (email)
                          </span>
                        )}
                        {previewData.dndSmsCount > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-600">
                            {previewData.dndSmsCount} DND (SMS)
                          </span>
                        )}
                      </div>
                    )}
                    <div className="max-h-40 overflow-y-auto text-xs text-gray-500 space-y-0.5">
                      {previewData.emails.map((email) => (
                        <div key={email}>{email}</div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* One-Off Email/SMS Compose (edit mode only) */}
            {editingId && eventType && (
              <>
                <div className="h-0 my-4 border-t-4 border-dotted border-gray-200" />
                <div className="flex items-center justify-between mb-2">
                  <p className="text-gray-400 text-left m-2">Send One-Off Message</p>
                  <button
                    type="button"
                    onClick={() => { setShowCompose(!showCompose); setSendResult(null); }}
                    className="px-3 py-1.5 rounded text-xs font-medium bg-orange-100 text-orange-700 hover:bg-orange-200"
                  >
                    {showCompose ? "Cancel" : "Compose"}
                  </button>
                </div>
                {sendResult && (
                  <div className={`mb-3 p-3 rounded-lg text-sm ${sendResult.testMode ? "bg-yellow-50 border border-yellow-200 text-yellow-700" : "bg-green-50 border border-green-200 text-green-700"}`}>
                    {sendResult.testMode ? "Test " : ""}
                    Queued: {sendResult.total} recipients in {sendResult.totalBatches} batch{sendResult.totalBatches !== 1 ? "es" : ""}
                    {sendResult.testMode && sendResult.originalRecipientCount && (
                      <span className="ml-2">(would send to {sendResult.originalRecipientCount})</span>
                    )}
                  </div>
                )}
                {activeBatchGroupId && batchJobs && batchJobs.length > 0 && (() => {
                  const completed = batchJobs.filter((j) => j.status === "complete").length;
                  const failed = batchJobs.filter((j) => j.status === "failed").length;
                  const processing = batchJobs.filter((j) => j.status === "processing").length;
                  const totalEmail = batchJobs.reduce((s, j) => s + (j.emailSuccessCount || 0), 0);
                  const totalSms = batchJobs.reduce((s, j) => s + (j.smsSuccessCount || 0), 0);
                  const totalDndSkipped = batchJobs.reduce((s, j) => s + (j.dndSkippedCount || 0), 0);
                  const totalRecipients = batchJobs.reduce((s, j) => s + (j.recipients?.length || 0), 0);
                  const allDone = (completed + failed) === batchJobs.length;
                  const nextPending = batchJobs.find((j) => j.status === "pending");
                  const nextAt = nextPending?.scheduledSendAt;
                  let minutesLeft = null;
                  if (nextAt && nextAt.seconds) {
                    minutesLeft = Math.max(0, Math.round((nextAt.seconds * 1000 - Date.now()) / 60000));
                  }
                  const barColor = failed > 0 ? "bg-yellow-500" : allDone ? "bg-green-500" : "bg-indigo-500";
                  const bgColor = failed > 0 ? "bg-yellow-50 border-yellow-200" : allDone ? "bg-green-50 border-green-200" : "bg-indigo-50 border-indigo-200";
                  return (
                    <div className={`mb-3 p-3 rounded-lg border text-sm ${bgColor}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">
                          Batch {completed + failed}{processing > 0 ? `+${processing}` : ""}/{batchJobs.length}
                          {failed > 0 && <span className="text-red-600 ml-1">({failed} failed)</span>}
                        </span>
                        <span className="text-xs text-gray-500">
                          {totalEmail > 0 && `${totalEmail} emails`}
                          {totalEmail > 0 && totalSms > 0 && " + "}
                          {totalSms > 0 && `${totalSms} SMS`}
                          {totalEmail + totalSms > 0 && ` / ${totalRecipients}`}
                          {totalDndSkipped > 0 && ` (${totalDndSkipped} DND skipped)`}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${barColor}`}
                          style={{ width: `${Math.round(((completed + failed) / batchJobs.length) * 100)}%` }}
                        />
                      </div>
                      {!allDone && minutesLeft !== null && (
                        <p className="text-xs text-gray-500 mt-1">
                          Next batch in ~{minutesLeft} min
                        </p>
                      )}
                      {allDone && failed === 0 && (
                        <p className="text-xs text-green-600 mt-1 font-medium">
                          All batches complete
                        </p>
                      )}
                      {allDone && failed > 0 && (
                        <p className="text-xs text-yellow-700 mt-1 font-medium">
                          Done — {failed} batch{failed !== 1 ? "es" : ""} failed (check logs)
                        </p>
                      )}
                    </div>
                  );
                })()}
                {showCompose && (
                  <div className="mb-4 rounded-lg border border-gray-200 p-4 space-y-3">
                    {/* Channel Toggle */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Channel</label>
                      <span className="isolate inline-flex rounded-md">
                        {["email", "sms", "both"].map((ch) => (
                          <button
                            key={ch}
                            type="button"
                            onClick={() => setComposeChannel(ch)}
                            className={`relative inline-flex items-center px-3 py-1.5 text-xs font-medium ring-1 ring-inset first:rounded-l-md last:rounded-r-md ${
                              composeChannel === ch
                                ? "bg-indigo-100 text-indigo-700 ring-indigo-600"
                                : "bg-white text-gray-500 ring-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {ch === "both" ? "Email + SMS" : ch.toUpperCase()}
                          </button>
                        ))}
                      </span>
                    </div>

                    {/* From Address */}
                    {(composeChannel === "email" || composeChannel === "both") && (
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">From</label>
                        <select
                          value={composeFrom}
                          onChange={(e) => setComposeFrom(e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        >
                          <option value="noreply">noreply@notify.insuranceagencytraining.org</option>
                          <option value="outreach">ben@outreach.insuranceagencytraining.org</option>
                          <option value="default">info@insuranceagencytraining.org</option>
                        </select>
                      </div>
                    )}

                    {/* Email Fields */}
                    {(composeChannel === "email" || composeChannel === "both") && (
                      <>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Subject</label>
                          <input
                            type="text"
                            value={composeSubject}
                            onChange={(e) => setComposeSubject(e.target.value)}
                            placeholder="Email subject line"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Body (HTML)</label>
                          <textarea
                            value={composeBody}
                            onChange={(e) => setComposeBody(e.target.value)}
                            placeholder="<p>Your email content here...</p>"
                            rows={6}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 font-mono text-xs"
                          />
                        </div>
                      </>
                    )}

                    {/* SMS Fields */}
                    {(composeChannel === "sms" || composeChannel === "both") && (
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          SMS Body <span className={`ml-1 ${composeSmsBody.length > 160 ? "text-red-500" : "text-gray-400"}`}>({composeSmsBody.length}/160)</span>
                        </label>
                        <textarea
                          value={composeSmsBody}
                          onChange={(e) => setComposeSmsBody(e.target.value)}
                          placeholder="Your SMS message..."
                          rows={3}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    )}

                    {/* Send Buttons */}
                    {(() => {
                      const emailReady = composeChannel === "email" || composeChannel === "both" ? (composeSubject && composeBody) : true;
                      const smsReady = composeChannel === "sms" || composeChannel === "both" ? !!composeSmsBody : true;
                      const canSend = !sending && emailReady && smsReady;
                      return (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleSendOneOff({ testMode: true })}
                            disabled={!canSend}
                            className="flex-1 py-2 rounded-md text-sm font-medium bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-50"
                          >
                            {sending ? "Sending..." : "Send Test"}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSendOneOff()}
                            disabled={!canSend}
                            className="flex-1 py-2 rounded-md text-sm font-medium bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
                          >
                            {sending ? "Sending..." : "Send to All"}
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </>
            )}

            {/* Sent Campaign Audit Log (edit mode only) */}
            {editingId && sentCampaigns.length > 0 && (
              <>
                <div className="h-0 my-4 border-t-4 border-dotted border-gray-200" />
                <p className="text-gray-400 text-left m-2">Sent Emails ({sentCampaigns.length})</p>
                <div className="mb-4 rounded-lg border border-gray-200 divide-y divide-gray-100 max-h-48 overflow-y-auto">
                  {sentCampaigns.map((c) => (
                    <div key={c.id} className="px-4 py-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700 truncate">{c.subject}</span>
                        <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                          {c.sentAt && c.sentAt.seconds
                            ? new Date(c.sentAt.seconds * 1000).toLocaleString("en-US", {
                                month: "short", day: "numeric", hour: "numeric",
                                minute: "2-digit", hour12: true,
                              })
                            : ""}
                        </span>
                      </div>
                      <div className="flex gap-2 mt-1 text-xs text-gray-400">
                        {c.batchGroupId ? (
                          <>
                            <span>{c.recipientCount} queued ({c.totalBatches} batch{c.totalBatches !== 1 ? "es" : ""})</span>
                            {c.testMode && <span className="text-yellow-600">TEST</span>}
                            {c.channel && c.channel !== "email" && <span className="text-sky-600">{c.channel.toUpperCase()}</span>}
                          </>
                        ) : (
                          <>
                            <span>{c.successCount}/{c.recipientCount} sent</span>
                            {c.failedCount > 0 && (
                              <span className="text-red-500">{c.failedCount} failed</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={saving || !eventType || !title || !date || !startTime || !finishTime}
              className={`flex flex-row ${!eventType ? "bg-gray-400" : "bg-green-600"} text-gray-200 py-2 px-4 w-full justify-center rounded-md focus:outline-none focus:border-green-700 disabled:opacity-50`}
            >
              {saving ? "Saving..." : editingId ? "Update Event" : "Schedule Event"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const NOTIFICATION_FIELDS = [
  { key: "notificationMondayBefore", label: "Mon" },
  { key: "notificationMorningOf", label: "AM" },
  { key: "notificationTenMinute", label: "10m" },
  { key: "notificationDayAfter", label: "Day" },
];

function notifIcon(status) {
  switch (status) {
    case "complete": return { icon: "\u2705", title: "Sent" };
    case "pending": return { icon: "\u23F3", title: "Pending" };
    case "paused": return { icon: "\u23F8\uFE0F", title: "Paused" };
    case "processing": return { icon: "\u23F3", title: "Processing" };
    case "queued": return { icon: "\uD83D\uDCE8", title: "Queued" };
    case "queued_test": return { icon: "\uD83E\uDDEA", title: "Queued (test)" };
    case "failed": return { icon: "\u274C", title: "Failed" };
    default: return { icon: "\u25CB", title: "Not set" };
  }
}

function EventRow({ event, onEdit, onDelete, onSync, past }) {
  const [syncing, setSyncing] = useState(false);
  const typeBadge = event.eventType === "IAT"
    ? "bg-blue-100 text-blue-700"
    : event.eventType === "I247"
    ? "bg-purple-100 text-purple-700"
    : "bg-gray-100 text-gray-600";

  const planLabel = formatRequiredPlans(event.requiredPlans);

  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${past ? "border-gray-200 bg-gray-50 opacity-60" : "border-gray-200 bg-white"}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className={`text-sm font-semibold ${past ? "text-gray-400" : "text-gray-900"}`}>{event.title}</p>
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${typeBadge}`}>
            {event.eventType}
          </span>
          {event.zoomMeetingId && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-sky-100 text-sky-700">
              Zoom
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {formatTimestamp(event.startTime)} — {formatTimestamp(event.finishTime)}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">Plans: {planLabel}</p>
        {event.notifications && Array.isArray(event.notifications) ? (
          <p className="text-xs text-gray-400 mt-0.5">
            {event.campaignPaused && (
              <span className="text-red-500 font-medium mr-1.5">PAUSED</span>
            )}
            Notifications:{" "}
            {event.notifications.map((n) => {
              const { icon, title } = notifIcon(n.status);
              return (
                <span key={n.id} title={`${n.label}: ${title}`} className="mr-1.5">
                  {icon} {n.label}
                </span>
              );
            })}
          </p>
        ) : NOTIFICATION_FIELDS.some((f) => event[f.key]) && (
          <p className="text-xs text-gray-400 mt-0.5">
            Notifications:{" "}
            {NOTIFICATION_FIELDS.map((f) => {
              const { icon, title } = notifIcon(event[f.key]);
              return (
                <span key={f.key} title={`${f.label}: ${title}`} className="mr-1.5">
                  {icon} {f.label}
                </span>
              );
            })}
          </p>
        )}
      </div>
      <div className="flex gap-2 ml-4">
        {event.zoomMeetingId && (
          <button
            onClick={async () => {
              setSyncing(true);
              await onSync(event);
              setSyncing(false);
            }}
            disabled={syncing}
            className="p-1.5 text-gray-400 hover:text-green-600 rounded disabled:opacity-50"
            title="Sync from Zoom"
          >
            <ArrowPathIcon className={`h-5 w-5 ${syncing ? "animate-spin" : ""}`} />
          </button>
        )}
        <button
          onClick={() => onEdit(event)}
          className="p-1.5 text-gray-400 hover:text-indigo-600 rounded"
          title="Edit"
        >
          <PencilSquareIcon className="h-5 w-5" />
        </button>
        <button
          onClick={() => onDelete(event)}
          className="p-1.5 text-gray-400 hover:text-red-600 rounded"
          title="Delete"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
